// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useAuthSession } from "../hooks/useAuthSession";
import { useSessionValidation } from "../hooks/useSessionValidation";
import { isDevPreviewMode } from "../utils/devEnv";
import { useSessionExpiredSignal } from "../utils/sessionExpirySignal";
import { useSignInFailure } from "../utils/signInFailureSignal";

const BLOCKING_INTERACTIONS: readonly string[] = [
  InteractionStatus.Startup,
  InteractionStatus.HandleRedirect,
  InteractionStatus.Logout,
];

export interface AuthGateState {
  devPreview: boolean;
  isAuthorized: boolean;
  isProcessing: boolean;
  isCheckingSession: boolean;
  isSessionExpired: boolean;
}

export function useAuthGate(): AuthGateState {
  const { inProgress } = useMsal();
  const hasMsalAccount = useIsAuthenticated();
  const { isAuthenticated } = useAuthSession();
  const isSessionChecked = useSessionValidation();
  const isSessionExpired = useSessionExpiredSignal();
  const signInFailure = useSignInFailure();
  const devPreview = isDevPreviewMode;

  const isSignedIn = hasMsalAccount && isAuthenticated;
  const isAuthorized = (devPreview || isSignedIn) && !isSessionExpired && signInFailure === null;
  const isProcessing =
    !devPreview &&
    (BLOCKING_INTERACTIONS.includes(inProgress) ||
      (inProgress !== InteractionStatus.None && !isAuthorized));
  const isCheckingSession = !devPreview && isAuthorized && !isSessionChecked;

  return { devPreview, isAuthorized, isProcessing, isCheckingSession, isSessionExpired };
}
